"use client"

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

type NavigationPendingContextValue = {
  /** True while at least one `<Link>` in this provider has an in-flight transition. */
  hasPending: boolean
  /**
   * Increment the pending count and return an `unregister` callback that
   * decrements. Designed for `useEffect` cleanup: call on mount when a Link's
   * `useLinkStatus().pending` is true and return the cleanup.
   */
  registerPending: () => () => void
}

const NavigationPendingContext = createContext<NavigationPendingContextValue>({
  hasPending: false,
  registerPending: () => () => {},
})

/**
 * Ref-counted "any sidebar/Link navigation in flight" signal. Wraps an org
 * shell so descendants — top progress bar, sidebar active-state — can react
 * to in-flight Link transitions without each having to hook `useLinkStatus`
 * themselves.
 */
export const NavigationPendingProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [count, setCount] = useState(0)
  const registerPending = useCallback(() => {
    setCount((c) => c + 1)
    return () => setCount((c) => c - 1)
  }, [])
  const value = useMemo<NavigationPendingContextValue>(
    () => ({ hasPending: count > 0, registerPending }),
    [count, registerPending],
  )
  return (
    <NavigationPendingContext.Provider value={value}>
      {children}
    </NavigationPendingContext.Provider>
  )
}

export const useNavigationPending = () => useContext(NavigationPendingContext)
